import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | pending
  const [tx, setTx] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const max = 8;
    const tick = async () => {
      try {
        const r = await api.get(`/billing/checkout-status/${sessionId}`);
        setTx(r.data);
        if (r.data.payment_status === "paid" || r.data.credited) {
          setStatus("paid");
          return;
        }
        if (r.data.status === "expired") {
          setStatus("failed");
          return;
        }
        attempts++;
        if (attempts >= max) {
          setStatus("pending");
          return;
        }
        setTimeout(tick, 2000);
      } catch {
        attempts++;
        if (attempts >= max) {
          setStatus("failed");
          return;
        }
        setTimeout(tick, 2000);
      }
    };
    tick();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <Card className="p-10 max-w-md w-full text-center border-slate-200 card-elevated">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-slate-400 mx-auto animate-spin" />
            <h1 className="font-heading text-2xl font-bold text-slate-900 mt-4">Verifico il pagamento…</h1>
            <p className="text-sm text-slate-500 mt-2">Stiamo aggiornando il tuo abbonamento, non chiudere la pagina.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
            <h1 className="font-heading text-2xl font-bold text-slate-900 mt-4" data-testid="payment-success">Pagamento confermato!</h1>
            <p className="text-sm text-slate-600 mt-2">Il tuo abbonamento è attivo. Grazie per aver scelto Incassi Giornalieri.</p>
            <Button className="mt-6 bg-slate-900 hover:bg-slate-800 text-white" onClick={() => nav("/admin")} data-testid="goto-admin">
              Vai alla tua area
            </Button>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-amber-500 mx-auto" />
            <h1 className="font-heading text-2xl font-bold text-slate-900 mt-4">Pagamento in elaborazione</h1>
            <p className="text-sm text-slate-600 mt-2">Riceverai una conferma via email a breve. Puoi accedere alla tua area: lo stato verrà aggiornato automaticamente.</p>
            <Button className="mt-6 bg-slate-900 hover:bg-slate-800 text-white" onClick={() => nav("/admin")}>Vai all'area</Button>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="w-14 h-14 text-red-600 mx-auto" />
            <h1 className="font-heading text-2xl font-bold text-slate-900 mt-4">Pagamento non completato</h1>
            <p className="text-sm text-slate-600 mt-2">Sembra che il pagamento sia stato annullato o sia fallito. Puoi riprovare dall'area abbonamento.</p>
            <div className="flex gap-2 justify-center mt-6">
              <Link to="/admin/abbonamento"><Button>Riprova</Button></Link>
              <Link to="/admin"><Button variant="outline">Vai all'area</Button></Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
